import React from "react";
import { Badge } from "@/components/ui/badge";

/* Status pill for the top-level order lifecycle — open / completed /
   voided / refunded / merged. Rendered on the order-detail header and
   inside the manage-orders table alongside the payment pill below. */
export function StatusPill({ status }) {
  const cfg = {
    open:            { label: "Open",              cls: "bg-amber-100 text-amber-800 border-amber-200" },
    completed:       { label: "Completed",         cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    voided:          { label: "Voided",            cls: "bg-zinc-200 text-zinc-700 border-zinc-300" },
    refunded:        { label: "Refunded",          cls: "bg-rose-100 text-rose-800 border-rose-200" },
    closed_no_show:  { label: "Closed (No-Show)",  cls: "bg-amber-100 text-amber-900 border-amber-300" },
  }[status] || { label: status, cls: "bg-zinc-100 text-zinc-700 border-zinc-200" };
  return <Badge variant="outline" className={`rounded-full text-[10px] font-medium ${cfg.cls}`}>{cfg.label}</Badge>;
}

/* Granular payment status pill — unpaid / partially_paid / paid / voided
   / refunded. Falls back to computing from amount_paid/total when the
   backend field isn't populated yet (legacy invoices). */
export function paymentStatusOf(order) {
  const explicit = order?.payment_status;
  if (explicit) return explicit;
  const status = order?.status;
  if (status === "voided") return "voided";
  if (status === "refunded") return "refunded";
  if (status === "merged") return "merged";
  if (status === "closed_no_show") return "no_show";
  const total = Number(order?.total || 0);
  const paid = Number(order?.amount_paid || 0);
  if (total <= 0.01) return status === "completed" ? "paid" : "unpaid";
  if (paid >= total - 0.01) return "paid";
  if (paid > 0.01) return "partially_paid";
  return "unpaid";
}

export function PaymentStatusPill({ order }) {
  const ps = paymentStatusOf(order);
  const cfg = {
    unpaid:          { label: "Unpaid",          cls: "bg-zinc-100 text-zinc-700 border-zinc-200" },
    partially_paid:  { label: "Partially Paid",  cls: "bg-amber-100 text-amber-800 border-amber-200" },
    paid:            { label: "Paid",            cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    voided:          { label: "Voided",          cls: "bg-zinc-200 text-zinc-700 border-zinc-300" },
    refunded:        { label: "Refunded",        cls: "bg-rose-100 text-rose-800 border-rose-200" },
    merged:          { label: "Merged",          cls: "bg-indigo-100 text-indigo-800 border-indigo-200" },
    no_show:         { label: "No-Show",         cls: "bg-amber-100 text-amber-900 border-amber-300" },
  }[ps] || { label: ps, cls: "bg-zinc-100 text-zinc-700 border-zinc-200" };
  return <Badge variant="outline" className={`rounded-full text-[10px] font-medium ${cfg.cls}`} data-testid={`payment-status-${ps}`}>{cfg.label}</Badge>;
}
